import _ from 'lodash';
import { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getLang } from '../../../i18n/l10n';
import getRootReducer from '../../../reducers';
import { selectUserDidReceivedUserData } from '../../../selectors/user/userPrimary.selector';
import { StateType } from '../../../states';
import { AnyObject } from '../../../types/utils';
import { LocalStorageMissionsKeys } from '../constants/storage';
import { MISSIONS_STATE_TRANSITION_DELAY } from '../constants/transitions';
import { MissionCardComplexeStatus, MissionCardComplexeStatuses } from '../types/components';

import useMissionComplexeStatus from './useMissionComplexeStatus';

type ExtendedSelectedProps<T> = T & { missionStatus: MissionCardComplexeStatus; shouldTransition: boolean };

const useMissionTransitionSelector = <T extends AnyObject>(
  selector: (state: StateType, props?: AnyObject) => T,
  selectorProps: AnyObject,
  { disabled } = { disabled: false },
): ExtendedSelectedProps<T> => {
  const reducer = getRootReducer({});
  const fakeState = JSON.parse(JSON.stringify(reducer({}, { type: '@@INIT' })));

  const hasReceivedUserDataYet = useSelector(selectUserDidReceivedUserData);

  const [readyToUpdate, setReadyToUpdate] = useState(disabled);

  useEffect(() => {
    const timeoutPid = setTimeout(() => {
      setReadyToUpdate(true);
    }, MISSIONS_STATE_TRANSITION_DELAY);

    return () => {
      if (timeoutPid) {
        clearTimeout(timeoutPid);
      }
    };
  }, []);

  const localCachedMissionsState: StateType = useMemo(() => {
    const user = JSON.parse(localStorage.getItem(LocalStorageMissionsKeys.USER) ?? '{}');
    const missions = JSON.parse(localStorage.getItem(LocalStorageMissionsKeys.MISSIONS) ?? '{}');
    const achievements = JSON.parse(localStorage.getItem(LocalStorageMissionsKeys.ACHIEVEMENTS) ?? '{}');
    const userMissions = JSON.parse(localStorage.getItem(LocalStorageMissionsKeys.USER_MISSIONS) ?? '{}');

    return {
      ...fakeState,
      user,
      entities: {
        missions,
        achievements,
      },
      userMissions,
    };
  }, [fakeState]);

  const reduxMissionsState = useSelector((state: StateType) => {
    return {
      ...fakeState,
      user: {
        connected: state.user.connected,
        nicknameJustChanged: state.user.nicknameJustChanged,
        promotionStatus: state.user.promotionStatus,
        missionsModerator: state.user.missionsModerator,
      },
      entities: {
        missions: state.entities.missions,
        achievements: state.entities.achievements,
      },
      userMissions: state.userMissions,
    };
  });

  /*
   * Used to prevent animating transitions from nothing to something
   * ie: the first time a user go the the extras tabs, we don't want him to see any transition
   */
  const hasLocalCacheAnyData = useMemo(
    () =>
      !!Object.keys(localCachedMissionsState?.user ?? {}).length ||
      !!Object.keys(localCachedMissionsState?.entities?.missions ?? {}).length ||
      !!Object.keys(localCachedMissionsState?.entities?.achievements ?? {}).length ||
      !!Object.keys(localCachedMissionsState?.userMissions ?? {}).length,
    [localCachedMissionsState],
  );

  const shouldSkipLocalState = useMemo(
    () => !reduxMissionsState.user.connected || hasReceivedUserDataYet || !hasLocalCacheAnyData,
    [reduxMissionsState, hasReceivedUserDataYet, hasLocalCacheAnyData],
  );

  const missionStatus = useMissionComplexeStatus(
    selectorProps,
    readyToUpdate && shouldSkipLocalState ? undefined : localCachedMissionsState,
  );

  const selectedProps = useMemo(
    () =>
      selector(readyToUpdate && shouldSkipLocalState ? reduxMissionsState : localCachedMissionsState, selectorProps),
    [selector, readyToUpdate, shouldSkipLocalState, reduxMissionsState, localCachedMissionsState, selectorProps],
  );

  const shouldTransition = useMemo(() => {
    const hasUserNicknameChanged = !!localCachedMissionsState.user.nicknameJustChanged;
    const hasLangChanged = localCachedMissionsState.user.lang !== getLang();
    const hasDifferences = !_.isEqual(reduxMissionsState, localCachedMissionsState);

    if (hasUserNicknameChanged) {
      localStorage.setItem(
        LocalStorageMissionsKeys.USER,
        JSON.stringify({
          ...localCachedMissionsState,
          user: {
            ...localCachedMissionsState.user,
            nicknameJustChanged: false,
          },
        }),
      );
    }

    return (
      !hasLangChanged &&
      hasReceivedUserDataYet &&
      reduxMissionsState.user.connected &&
      hasDifferences &&
      hasLocalCacheAnyData &&
      !disabled &&
      !hasUserNicknameChanged
    );
  }, [disabled, hasLocalCacheAnyData, localCachedMissionsState, reduxMissionsState, hasReceivedUserDataYet]);

  return useMemo(
    () => ({
      ...(selectedProps ?? {}),
      missionStatus: missionStatus ?? MissionCardComplexeStatuses.EXPIRED,
      shouldTransition,
    }),
    [selectedProps, missionStatus, shouldTransition],
  );
};

export default useMissionTransitionSelector;
